// APCA (Accessible Perceptual Contrast Algorithm — the WCAG 3 candidate) and color helpers shared by
// the /design gallery and the token gate. APCA reports Lc (lightness contrast, ~ -108..108): it is
// perceptually uniform, polarity-aware (negative = light-on-dark), and the correct tool for dark mode
// where WCAG2's 4.5:1 over/under-states. Canonical APCA-W3 0.1.9 public formula (Andrew Somers).
const TRC = 2.4, Rco = 0.2126729, Gco = 0.7151522, Bco = 0.072175;
const BT = 0.022, BC = 1.414, SCALE = 1.14, OFF = 0.027, LOCLIP = 0.1, DMIN = 0.0005;

const lum = ([r, g, b]) => Rco * (r / 255) ** TRC + Gco * (g / 255) ** TRC + Bco * (b / 255) ** TRC;

// txt/bg are [r,g,b] 0..255. Returns Lc.
export function apcaLc(txt, bg) {
  let yt = lum(txt), yb = lum(bg);
  if (yt <= BT) yt += (BT - yt) ** BC;
  if (yb <= BT) yb += (BT - yb) ** BC;
  if (Math.abs(yb - yt) < DMIN) return 0;
  let o;
  if (yb > yt) { const s = (yb ** 0.56 - yt ** 0.57) * SCALE; o = s < LOCLIP ? 0 : s - OFF; }
  else { const s = (yb ** 0.65 - yt ** 0.62) * SCALE; o = s > -LOCLIP ? 0 : s + OFF; }
  return o * 100;
}

// "R G B" channel string -> [r,g,b]
export const channels = (str) => String(str).trim().split(/\s+/).map(Number);
export const hexToRgb = (h) => {
  const m = String(h).replace("#", "");
  const n = parseInt(m.length === 3 ? m.split("").map((c) => c + c).join("") : m, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// APCA grade for a Lc value (matches the mandate's Lc≥75 body / ≥60 large targets).
export const apcaGrade = (lc) => {
  const a = Math.abs(lc);
  return a >= 90 ? "AAA" : a >= 75 ? "body" : a >= 60 ? "large" : a >= 45 ? "headline" : a >= 30 ? "faint" : "fail";
};

// Tenant brand override: pick the foreground (white or deep ink) that maximizes |Lc| on an arbitrary
// accent — the documented white-label a11y fix (never white-on-light buttons). Returns {fg, lc}.
export function bestTextOn(rgb, options = [[255, 255, 255], [15, 23, 42]]) {
  let best = options[0], bestLc = -1;
  for (const o of options) { const lc = Math.abs(apcaLc(o, rgb)); if (lc > bestLc) { bestLc = lc; best = o; } }
  return { fg: best, lc: bestLc };
}
